import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import * as Lucide from "lucide-react";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import UsageIndicator from "@/components/UsageIndicator";

export function UserToolsList() {
  const [tools, setTools] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/tools").then((r) => setTools(r.data)); }, []);
  const visible = tools.filter((t) => !q || t.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <UserLayout>
      <h1 className="bx-heading text-3xl font-extrabold">All AI Tools</h1>
      <p className="text-slate-400 text-sm mt-1">Browse and run any tool — credits deduct only after a successful generation.</p>
      <UsageIndicator />
      <div className="relative mt-5 max-w-md">
        <Lucide.Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="bx-input pl-9" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
        {visible.map((t, i) => {
          const Icon = require("lucide-react")[t.icon] || Lucide.Sparkles;
          return (
            <Link key={t.id} to={`/tools/${t.slug}`} data-testid={`udash-tool-${t.slug}`} className="bx-card p-5">
              <span className={`bx-glow-icon ${["", "cyan", "blue", "emerald", "pink", "amber"][i % 6]}`}><Icon size={18} /></span>
              <div className="bx-heading mt-3 font-semibold">{t.name}</div>
              <p className="text-xs text-slate-400 mt-1 line-clamp-2">{t.description}</p>
              <div className="text-xs text-purple-300 font-mono mt-3">{t.credit_cost} credits</div>
            </Link>
          );
        })}
      </div>
    </UserLayout>
  );
}

export function UserHistory() {
  const [gens, setGens] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/generations").then((r) => setGens(r.data)); }, []);
  return (
    <UserLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Generation History</h1>
      <p className="text-slate-400 text-sm mt-1">All your AI generations in one place.</p>
      <div className="grid lg:grid-cols-[420px_1fr] gap-5 mt-6">
        <div className="bx-card p-3 max-h-[70vh] overflow-auto bx-scrollbar">
          {gens.length === 0 && <div className="text-slate-500 text-sm p-4">No generations yet.</div>}
          {gens.map((g) => (
            <button key={g.id} onClick={() => setActive(g)} data-testid={`history-${g.id}`} className={`block w-full text-left p-3 rounded-lg ${active?.id === g.id ? "bg-purple-600/15 border border-purple-500/40" : "hover:bg-white/5"}`}>
              <div className="text-xs text-purple-300">{g.tool_name}</div>
              <div className="text-sm font-medium mt-0.5 line-clamp-1">{g.prompt}</div>
              <div className="text-xs text-slate-500 mt-1">{new Date(g.created_at).toLocaleString()} · -{g.credits_used} cr</div>
            </button>
          ))}
        </div>
        <div className="bx-card p-5 min-h-[400px]">
          {!active && <div className="text-slate-500 text-center py-12">Select a generation to view.</div>}
          {active && (
            <div>
              <div className="text-xs text-purple-300">{active.tool_name}</div>
              <div className="bx-heading text-xl font-bold mt-1">{active.prompt}</div>
              <div className="bx-divider my-4" />
              {active.kind === "image" && active.image_data_url ? (
                <img src={active.image_data_url} alt="" className="w-full rounded-xl border border-white/10" />
              ) : (
                <Markdown>{active.output}</Markdown>
              )}
            </div>
          )}
        </div>
      </div>
    </UserLayout>
  );
}

export function UserCreditHistory() {
  const [tx, setTx] = useState([]);
  useEffect(() => { api.get("/credits/transactions?limit=500").then((r) => setTx(r.data)); }, []);
  return (
    <UserLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Credit History</h1>
      <p className="text-slate-400 text-sm mt-1">Every credit added or used on your account.</p>
      <div className="bx-card mt-6 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
            <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Type</th><th className="text-left p-3">Note</th><th className="text-right p-3">Amount</th></tr>
          </thead>
          <tbody>
            {tx.map((t) => (
              <tr key={t.id} className="border-t border-white/5">
                <td className="p-3 text-slate-400">{new Date(t.created_at).toLocaleString()}</td>
                <td className="p-3"><span className="bx-chip" style={{ fontSize: 10 }}>{t.kind}</span></td>
                <td className="p-3">{t.note}</td>
                <td className={`p-3 text-right font-mono ${t.amount >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </UserLayout>
  );
}

export function UserInvoices() {
  const [inv, setInv] = useState([]);
  useEffect(() => { api.get("/invoices").then((r) => setInv(r.data)); }, []);
  return (
    <UserLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Invoices</h1>
      <div className="bx-card mt-6 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
            <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Gateway</th><th className="text-right p-3">Amount</th><th className="text-right p-3">Status</th></tr>
          </thead>
          <tbody>
            {inv.map((i) => (
              <tr key={i.id} className="border-t border-white/5">
                <td className="p-3 text-slate-400">{new Date(i.created_at).toLocaleDateString()}</td>
                <td className="p-3">{i.plan_name}</td>
                <td className="p-3 capitalize">{i.gateway}</td>
                <td className="p-3 text-right">${i.amount_usd.toFixed(2)}</td>
                <td className="p-3 text-right"><span className="bx-chip" style={{ fontSize: 10 }}>{i.status}</span></td>
              </tr>
            ))}
            {!inv.length && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </UserLayout>
  );
}

export function UserSupport() {
  const [tickets, setTickets] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/support/tickets", { subject, message }); toast.success("Ticket created"); setSubject(""); setMessage(""); load(); }
    catch { toast.error("Could not create ticket"); }
  };
  return (
    <UserLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Support</h1>
      <p className="text-slate-400 text-sm mt-1">Create a ticket, our team replies within 24 hours.</p>
      <form onSubmit={create} className="bx-card p-5 mt-6 space-y-3 max-w-2xl">
        <input data-testid="ticket-subject" required placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="bx-input" />
        <textarea data-testid="ticket-message" required rows={4} placeholder="Describe your issue…" value={message} onChange={(e) => setMessage(e.target.value)} className="bx-input resize-y" />
        <button data-testid="ticket-submit" className="bx-btn-primary">Create Ticket</button>
      </form>
      <div className="mt-8">
        <div className="bx-heading text-lg font-semibold mb-3">Your tickets</div>
        <div className="space-y-3">
          {tickets.map((t) => (
            <div key={t.id} className="bx-card p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold">{t.subject}</div>
                <span className="bx-chip" style={{ fontSize: 10 }}>{t.status}</span>
              </div>
              <p className="text-sm text-slate-400 mt-2">{t.message}</p>
              <div className="text-xs text-slate-500 mt-2">{new Date(t.created_at).toLocaleString()}</div>
            </div>
          ))}
          {!tickets.length && <div className="text-slate-500 text-sm">No tickets yet.</div>}
        </div>
      </div>
    </UserLayout>
  );
}

export function UserSettings() {
  const { user, refresh, logout } = useAuth();
  const [name, setName] = useState(user?.name || "");
  useEffect(() => { setName(user?.name || ""); }, [user]);
  const save = async () => {
    try { await api.patch("/auth/me", { name }); await refresh(); toast.success("Saved"); }
    catch { toast.error("Save failed"); }
  };
  return (
    <UserLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Account Settings</h1>
      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="bx-card p-5">
          <div className="bx-heading text-lg font-semibold">Profile</div>
          <div className="mt-3 space-y-3">
            <div>
              <label className="text-xs text-slate-400">Email</label>
              <input value={user?.email || ""} disabled className="bx-input mt-1 opacity-70" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Name</label>
              <input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className="bx-input mt-1" />
            </div>
            <button data-testid="settings-save" onClick={save} className="bx-btn-primary">Save</button>
          </div>
        </div>
        <div className="bx-card p-5">
          <div className="bx-heading text-lg font-semibold">Plan & Credits</div>
          <div className="mt-3 text-sm text-slate-300">Current plan: <span className="capitalize text-purple-300">{user?.plan || "starter"}</span></div>
          <div className="text-sm text-slate-300">Balance: <span className="text-purple-300 font-mono">{user?.credits?.toLocaleString()}</span></div>
          <div className="mt-4 flex gap-3">
            <Link to="/buy-credits" className="bx-btn-primary">Buy Credits</Link>
            <button onClick={logout} className="bx-btn-ghost">Log out</button>
          </div>
        </div>
      </div>
    </UserLayout>
  );
}
